import { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Bounce, toast } from "react-toastify";
import { Form, Row, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { faWarning } from "@fortawesome/free-solid-svg-icons";
import {
  PageHeader,
  Loading,
  toastSuccessMessage,
  updateUserCurrentStep,
  downloadFileFromResponse,
} from "wup-module1-base";
import SiteQuestions from "../SiteQuestions/SiteQuestions";
import { AlertWarning } from "../../Components/Alert";
import {
  PageTitleConstants,
  ProcessStatusEnum,
  QuestionnairesEnum,
} from "../../Constants";
import UserContext from "../../Contexts/UserContext";
import { SurveyService, AuthService, ConfigService } from "../../Services";
import { DateHelpers } from "../../Helpers/Date/DateHelpers";

const isTypeActive = (type, data) => {
  const foundObject = data?.find(
    (obj) => obj.type === type && obj.activate === true
  );
  return !!foundObject;
};

function CompletingBalanceSheetOfModuleSecond({
  surveyType = QuestionnairesEnum.Module2.value,
  voucherAmount = 100
}) {
  const [surveyData, setSurveyData] = useState(null);
  const { user, setUser } = useContext(UserContext);
  const [surveyAnswersData, setSurveyAnswersData] = useState(null);
  const [isFinal, setIsFinal] = useState(false);
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [areasHelper, setAreasHelper] = useState();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isSubmitAvailable, setIsSubmitAvailable] = useState(false);
  const [developmentAreas, setDevelopmentAreas] = useState([]);
  const [invalidCourses, setInvalidCourses] = useState([]);
  const saveDataWithIntervalsRef = useRef();
  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    reset,
    trigger,
    unregister,
    watch,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    mode: "all",
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [surveyId, setSurveyId] = useState(null);

  /**
  * Checks if the current module is closed for orders.
  * Shows a warning and redirects if the deadline has passed.
  */
  const checkIfModuleIsClosed = (callback = () => { }) => {
    ConfigService.getDeadlinesFormattedToContext(user).then((response) => {
      let isNotInScope = false;
      let isEnabled = false;
      let endDateToShow = "";

      const contractMap = { "1": "First", "2": "Second" };
      const contractRecruitmentTypes = user.contractRecruitmentTypes;

      if (surveyType === QuestionnairesEnum.Module2.value) {
        const type = contractRecruitmentTypes["1"];
        const typeName = type.charAt(0).toUpperCase() + type.slice(1);

        isNotInScope = !response[`is${contractMap["1"]}Contract${typeName}InDeadlineScope`];
        isEnabled = response[`is${contractMap["1"]}Contract${typeName}DeadlineEnabled`];
        endDateToShow = response.firstContractTo;
      }

      if (surveyType === QuestionnairesEnum.Module2v2.value) {
        const type = contractRecruitmentTypes["2"];
        const typeName = type.charAt(0).toUpperCase() + type.slice(1);

        isNotInScope = !response[`is${contractMap["2"]}Contract${typeName}InDeadlineScope`];
        isEnabled = response[`is${contractMap["2"]}Contract${typeName}DeadlineEnabled`];
        endDateToShow = response.secondContractTo;
      }

      if (isNotInScope && isEnabled) {
        const message = `${DateHelpers.formatFullDateHours(endDateToShow)} zakończyliśmy przyjmowanie zamówień na bony.Zgodnie z 
          obowiązującym regulaminem nie masz już możliwości złożenia  zamówienia na bony w projekcie
          "Małopolski pociąg do kariery - sezon 1"`;

        toast.warn(message, {
          position: "top-right",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });

        navigate('/');
        return;
      }

      callback();
    });
  }

  useEffect(() => {
    checkIfModuleIsClosed()
  }, []);

  function onSubmit() {
    checkIfModuleIsClosed(() => {
      if (!isSubmitAvailable) {
        return
      };

      setIsSubmitLoading(true);
      const rawAnswersObj = {
        tasks: JSON.stringify(areasHelper),
        areas: JSON.stringify(developmentAreas)
      }
      const requestData = {
        questionnaire_id: surveyId,
        client_id: user.id,
        answers: JSON.stringify(setNewIndexForTable(getValues())),
        final: true,
        complete_answers: getAnswersCount().toString(),
        complete_pages: userCurrentPage.toString(),
        raw_answers_data: JSON.stringify(rawAnswersObj),
      };

      SurveyService.storeSurvey(requestData, user.id, surveyId)
        .then((response) => {
          fetchUserData();
          toastSuccessMessage("Dane przesłane pomyślnie");
          navigate(`/ `);
        })
        .catch((error) => {
          if (error.response?.data?.type === "NoAreaQuestion") {
            toast.error(
              `Nie wybrano żadnych szkoleń.Kliknij "Wstecz", aby przejść do ekranu gdzie je wybierzesz`
            );
          } else if (error.response?.data?.type === "FinalAnswer") {
            toast.error(`Ankieta została już wypełniona`);
          } else if (error?.response?.data?.message) {
            toast.error(error.response.data.message);
          } else {
            toast.error("Coś poszło nie tak");
          }
          console.error(error);
        })
        .finally(() => {
          setIsSubmitLoading(false);
        });
    });
  }

  const fetchData = () => {
    SurveyService.fetchSurvey(surveyId)
      .then((response) => {
        setSurveyData(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  //funkcja pobierająca pdf
  const downloadPDF = () => {
    SurveyService.downloadAnswersPDF(user.id, surveyId)
      .then((res) => {
        downloadFileFromResponse(res);
        toastSuccessMessage("Pobieranie pliku");
      })
      .catch((error) => {
        toast.error("Błąd pobrania PDF");
      });
  };

  //fetch aktualnych danych klienta
  const fetchUserData = () => {
    AuthService.getUserData()
      .then((response) => {
        const updatedUser = updateUserCurrentStep(user, response.data.status);
        setUser(updatedUser);
      })
      .catch((error) => { });
  };

  //funkcja zwracajaca na ile pytan uzytkownik udzielil odpowiedzi
  const getAnswersCount = () => {
    let answeredQuestions = 0;
    let answeredQuestionIds = new Set();
    let data = getValues();
    for (const key in data) {
      if (key.includes("_")) {
        const questionId = key.split("_")[0];
        if (!answeredQuestionIds.has(questionId) && data[key] !== "") {
          answeredQuestions++;
          answeredQuestionIds.add(questionId);
        }
      }
    }
    for (const key in data) {
      if (!key.includes("_") && data[key] !== "") {
        answeredQuestions++;
      }
    }

    return answeredQuestions;
  };

  //pomocnicza funkcja reindeksująca tworząca jedną tablice dla tabel która wstawiamy do odpowiedzi
  function setDataToUpdate(data) {
    let table = [];
    for (let key in data) {
      for (let subKey in data[key]) {
        let index = subKey;
        let valueKeys = Object.keys(data[key][subKey]);
        for (let i = 0; i < valueKeys.length; i++) {
          let valueKey = valueKeys[i];
          let parts = valueKey.split("_");
          parts[parts.length - 1] = index;
          let newKey = parts.join("_");
          data[key][subKey][newKey] = data[key][subKey][valueKey];
          if (newKey !== valueKey) {
            delete data[key][subKey][valueKey];
          }
        }
        table.push(data[key][subKey]);
      }
    }

    const obj = table.reduce((acc, curr) => {
      return { ...acc, ...curr };
    }, {});

    return obj;
  }

  //funkcja reindeksujaca czyści stare klucze i ustawia nowe
  function setNewIndexForTable(data) {
    for (const key in data) {
      if (key.startsWith("answer")) {
        delete data[key];
      }
      if (key.endsWith("table_0")) {
        delete data[key];
      }
    }

    const groupedData = {};

    for (const key in data) {
      if (key.split("_").length === 3) {
        const [selector, , lastIndexValue] = key.split("_");
        groupedData[selector] = groupedData[selector] || {};
        groupedData[selector][lastIndexValue] = {
          ...groupedData[selector][lastIndexValue],
          [key]: data[key],
        };
        delete data[key];
      }
    }

    const reindexedObject = {};

    for (const [key, value] of Object.entries(groupedData)) {
      reindexedObject[key] = {};
      let index = 1;
      for (const innerValue of Object.values(value)) {
        reindexedObject[key][index] = innerValue;
        index++;
      }
    }
    const updatedData = setDataToUpdate(reindexedObject);

    return { ...data, ...updatedData };
  }

  const saveDataWithIntervals = useCallback(() => {
    const rawAnswersObj = {
      tasks: JSON.stringify(areasHelper)
    };
    const requestData = {
      questionnaire_id: surveyId,
      client_id: user.id,
      answers: JSON.stringify(setNewIndexForTable(getValues())),
      final: false,
      complete_answers: getAnswersCount().toString(),
      complete_pages: userCurrentPage.toString(),
      raw_answers_data: JSON.stringify(rawAnswersObj),
    };
    SurveyService.storeSurvey(requestData, user.id, surveyId)
      .then((response) => {
      })
      .catch((error) => {
        console.error(error);
      });
  }, [areasHelper, surveyId, user.id, getValues, getAnswersCount, userCurrentPage, SurveyService]);

  useEffect(() => {
    saveDataWithIntervalsRef.current = saveDataWithIntervals;
  }, [saveDataWithIntervals]);

  const checkAreaCourses = (areasString) => {

    const courses = areasString?.courses?.trim?.() ?? "";
    if (!courses) {
      setInvalidCourses([]);
      return;
    }

    SurveyService.checkCourses(areasString).then((res) => {
      const invalid = res?.data?.invalidCourses ?? [];
      setInvalidCourses(invalid);
      if (invalid.length > 0) {
        notifySelectedCourseChangedName();
      }
    }).catch(() => {
      setInvalidCourses([]);
    });
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      saveDataWithIntervalsRef.current();
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const fetchAnswersData = () => {
    SurveyService.fetchAnswers(user.id, surveyId)
      .then((response) => {
        setSurveyAnswersData(response.data.answers);

        let rawDataObj = JSON.parse(response.data?.raw_answers_data);
        if (!!rawDataObj) {
          !!rawDataObj.tasks && setAreasHelper(JSON.parse(rawDataObj?.tasks));
          !!rawDataObj.areas && setDevelopmentAreas(JSON.parse(rawDataObj?.areas));
        }

        if (!!response.data.final) {
          setIsFinal(response.data.final);
        }

        if (response.data.complete_pages) {
          setUserCurrentPage(parseInt(response.data.complete_pages));
        } else {
          setUserCurrentPage(1);
        }
      })
      .catch((error) => {
        setUserCurrentPage(1);
        saveDataWithIntervalsRef.current();
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const findAreaSelector = (data) => {
    for (const page of data.pages) {
      for (const question of page.questions) {
        if (question.type === 12) {
          return question.selector_id;
        }
      }
    }
    return null;
  }

  const checkAreas = () => {
    let areaSelector = findAreaSelector(surveyData);
    let parsedAnswers = getValues();

    areaSelector && checkAreaCourses({ courses: parsedAnswers[areaSelector] });
  }

  useEffect(() => {
    if (!!surveyAnswersData && !!surveyData) {
      reset(JSON.parse(surveyAnswersData));

      checkAreas();
    }
  }, [surveyAnswersData]);

  useEffect(() => {
    if (!isFinal && surveyId != null) {
      const interval = setInterval(() => {
        saveDataWithIntervalsRef.current();
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [isFinal, surveyId, userCurrentPage]);

  useEffect(() => {
    if (surveyId) fetchData();
  }, [surveyId]);

  useEffect(() => {
    if (surveyData) fetchAnswersData();
  }, [surveyData]);

  useEffect(() => {
    SurveyService.fetchTypes()
      .then((response) => {
        if (!isTypeActive(3, response.data)) {
          navigate("/");
        }
        const survey = response.data.find(
          (s) => s.type === surveyType
        );
        if (!!survey) setSurveyId(survey.id);
      })
      .catch((error) => { });
  }, []);

  const notifySelectedCourseChangedName = () => toast.error(() => {
    return (
      <div>
        Wybrane przez Ciebie szkolenia{" "}
        {invalidCourses.map((answer, index) => (
          <div key={index}>
            <strong>{answer}</strong>
            {index < invalidCourses.length - 1 && ", "}
          </div>
        ))}{" "}
        zmieniły kategorię/podkategorię lub nie są już dostępne w systemie. Usuń je z listy i spróbuj wyszukać na nowo.
        W razie problemów skontaktuj się z nami.
      </div>
    );
  }, {
    position: "top-right",
    className: "toast-danger",
  });

  return (
    <>
      <Container>
        <PageHeader
          content={surveyType === QuestionnairesEnum.Module2.value ? PageTitleConstants.CompletingBalanceSheetOfModuleSecond : PageTitleConstants.CompletingBalanceSheetOfModuleSecondv2}
        />
        {surveyType !== QuestionnairesEnum.Module2.value && user.currentStep !== ProcessStatusEnum.ContractFormv2.value && voucherAmount > 0 && (<div className="application-form-container survey-screen">
          <div className="info-container">
            <div className="description-element">
              <FontAwesomeIcon
                icon={faStar}
                className="description-icon"
              />
              <div class="description-paragraph" data-id="33:4107">
                <div>
                  Wypełnij ankietę i zamów pozostałe <strong>{voucherAmount}</strong> bonów!
                </div>
                <br></br>
              </div>
            </div>
          </div>
        </div>)}
        <Form onSubmit={handleSubmit(onSubmit)}>
          {!!isLoading || !userCurrentPage ? (
            <Loading />
          ) : (
            surveyData && (
              <>
                {invalidCourses.length > 0 && !isFinal && (
                  <>
                    <div className="area-warning">
                      <AlertWarning>
                        <div className="row">
                          <FontAwesomeIcon icon={faWarning} className="description-icon" />
                          <div className="description-paragraph" data-id="33:4107">
                            Wybrane przez Ciebie szkolenia{" "}
                            {invalidCourses.map((answer, index) => (
                              <div>
                                <strong>{answer}</strong>
                                {index < invalidCourses.length - 1 && ", "}
                              </div>
                            ))}{" "}
                            zmieniły kategorię/podkategorię lub nie są już dostępne w systemie. Usuń je z listy i spróbuj wyszukać na nowo.
                            W razie problemów skontaktuj się z nami.
                          </div>
                        </div>
                      </AlertWarning>
                    </div>
                  </>
                )}
                <Row className="mt-1">
                  <SiteQuestions
                    surveyArray={surveyData.pages}
                    register={register}
                    getValues={getValues}
                    answers={answers}
                    setAnswers={setAnswers}
                    errors={errors}
                    unregister={unregister}
                    watch={watch}
                    setValue={setValue}
                    surveyAnswersData={surveyAnswersData}
                    isFinal={isFinal}
                    trigger={trigger}
                    isValid={isValid}
                    setUserCurrentPage={setUserCurrentPage}
                    userCurrentPage={userCurrentPage}
                    setError={setError}
                    clearErrors={clearErrors}
                    saveDataWithIntervals={saveDataWithIntervalsRef.current}
                    survType={surveyType}
                    voucherAmount={voucherAmount}
                    downloadPDF={downloadPDF}
                    reset={reset}
                    isSubmitLoading={isSubmitLoading}
                    setAreasHelper={setAreasHelper}
                    isSubmitAvailable={isSubmitAvailable}
                    setIsSubmitAvailable={setIsSubmitAvailable}
                    developmentAreas={developmentAreas}
                    setDevelopmentAreas={setDevelopmentAreas}
                    checkAreas={checkAreas}
                    callbackOnClick={checkIfModuleIsClosed}
                  />
                </Row>
              </>
            )
          )}
        </Form>
      </Container>
    </>
  );
}

export default CompletingBalanceSheetOfModuleSecond;
